import React from 'react';
import type { ReportMode } from '../../../services/report.service';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePermissions } from '../../../hooks/usePermissions';

interface ReportModeSelectorProps {
  mode: ReportMode;
  onModeChange: (mode: ReportMode) => void;
  inventoryEntryMode: ReportMode;
  businessEntryMode: ReportMode;
}

// Spec 100: each report section now has its own read permission.
// Hide modes the user lacks read access to.
const MODE_PERMISSION: Record<ReportMode, string> = {
  roasting: 'reports.roasting',
  greenBeans: 'reports.green_beans',
  roastedBeans: 'reports.roasted_beans',
  finishedGoods: 'reports.finished_goods',
  business: 'reports.business',
  customer: 'reports.customer',
};

// PRD 86: the Inventory top tab groups the three item-level sub-modes.
const INVENTORY_SUB_MODES: ReportMode[] = ['greenBeans', 'roastedBeans', 'finishedGoods'];

// PRD 87: the Business top tab groups the Sales (existing business report) and Customer sub-modes.
const BUSINESS_SUB_MODES: ReportMode[] = ['business', 'customer'];

export const ReportModeSelector: React.FC<ReportModeSelectorProps> = ({ mode, onModeChange, inventoryEntryMode, businessEntryMode }) => {
  const { t } = useLanguage();
  const { can } = usePermissions();

  const canRead = (target: ReportMode) => can(MODE_PERMISSION[target], 'read');

  const isInventoryMode = INVENTORY_SUB_MODES.includes(mode);

  const visibleSubModes = INVENTORY_SUB_MODES.filter(canRead);
  const inventoryVisible = visibleSubModes.length > 0;
  // Clicking the Inventory tab returns to the last-used sub-mode (default greenBeans),
  // but only when the user can read it; otherwise fall back to the first available.
  const inventoryTarget = visibleSubModes.includes(inventoryEntryMode)
    ? inventoryEntryMode
    : visibleSubModes[0] ?? 'greenBeans';

  const isBusinessMode = BUSINESS_SUB_MODES.includes(mode);
  const visibleBusinessSubModes = BUSINESS_SUB_MODES.filter(canRead);
  const businessVisible = visibleBusinessSubModes.length > 0;
  const businessTarget = visibleBusinessSubModes.includes(businessEntryMode)
    ? businessEntryMode
    : visibleBusinessSubModes[0] ?? 'business';

  const topTabs: Array<{ key: string; label: string; description: string; active: boolean; visible: boolean; onClick: () => void; testId: string }> = [
    {
      key: 'roasting',
      label: t('reports.modes.roasting.label'),
      description: t('reports.modes.roasting.description'),
      active: mode === 'roasting',
      visible: canRead('roasting'),
      onClick: () => onModeChange('roasting'),
      testId: 'report-mode-roasting',
    },
    {
      key: 'inventory',
      label: t('reports.modes.inventory.label'),
      description: t('reports.modes.inventory.description'),
      active: isInventoryMode,
      visible: inventoryVisible,
      onClick: () => onModeChange(inventoryTarget),
      testId: 'report-mode-inventory',
    },
    {
      key: 'business',
      label: t('reports.modes.business.label'),
      description: t('reports.modes.business.description'),
      active: isBusinessMode,
      visible: businessVisible,
      onClick: () => onModeChange(businessTarget),
      testId: 'report-mode-business',
    },
  ];

  const subTabs: Array<{ value: ReportMode; label: string; testId: string }> = [
    { value: 'greenBeans', label: t('reports.modes.greenBeans.label'), testId: 'report-submode-green-beans' },
    { value: 'roastedBeans', label: t('reports.modes.roastedBeans.label'), testId: 'report-submode-roasted-beans' },
    { value: 'finishedGoods', label: t('reports.modes.finishedGoods.label'), testId: 'report-submode-finished-goods' },
  ];

  const visibleTopTabs = topTabs.filter(tab => tab.visible);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3" data-test-id="report-mode-selector">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {visibleTopTabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={tab.onClick}
            data-test-id={tab.testId}
            className={`text-left rounded-lg border px-3 py-3 transition-colors ${
              tab.active
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <p className="text-sm font-semibold">{tab.label}</p>
            <p className={`mt-1 text-xs ${tab.active ? 'text-primary-700' : 'text-gray-500'}`}>{tab.description}</p>
          </button>
        ))}
      </div>

      {isInventoryMode && inventoryVisible && (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3" data-test-id="report-inventory-subtabs">
          {subTabs
            .filter(subTab => canRead(subTab.value))
            .map(subTab => {
              const isActive = mode === subTab.value;
              return (
                <button
                  key={subTab.value}
                  type="button"
                  onClick={() => onModeChange(subTab.value)}
                  data-test-id={subTab.testId}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary-500 bg-primary-50 text-primary-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {subTab.label}
                </button>
              );
            })}
        </div>
      )}

      {isBusinessMode && businessVisible && (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3" data-test-id="report-business-subtabs">
          {([
            { value: 'business', label: t('reports.businessSubTabs.sales'), testId: 'report-submode-sales' },
            { value: 'customer', label: t('reports.businessSubTabs.customer'), testId: 'report-submode-customer' },
          ] as Array<{ value: ReportMode; label: string; testId: string }>)
            .filter(subTab => canRead(subTab.value))
            .map(subTab => {
              const isActive = mode === subTab.value;
              return (
                <button
                  key={subTab.value}
                  type="button"
                  onClick={() => onModeChange(subTab.value)}
                  data-test-id={subTab.testId}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary-500 bg-primary-50 text-primary-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {subTab.label}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};
